(function () {
  'use strict';

  const STORY_NODE_FIELDS = 'id display_name age age_bracket city state email phone country harvested_species harvest_type harvest_season harvest_date title description model_name shopify_product_id status reviewer_remarks is_featured is_admin_story is_hall_of_honour consent_age_confirmed consent_age_text consent_guardian_confirmed consent_guardian_text published_at user_id category_id category { id slug label sort_order } author { id name email } media { id url media_type sort_order created_at } created_at updated_at';
  const STORY_CONNECTION_FIELDS = `edges { cursor node { ${STORY_NODE_FIELDS} } } pageInfo { hasNextPage hasPreviousPage startCursor endCursor }`;
  const DROP_FIELDS = 'id shopifyCollectionId collectionTitle collectionHandle productsCount launchAt timezone quantity teaserDescription status createdAt updatedAt';
  const DROP_CONNECTION_FIELDS = `edges { cursor node { ${DROP_FIELDS} } } pageInfo { hasNextPage endCursor }`;
  const DROP_COLLECTION_FIELDS = 'id title handle productsCount';
  // is_founders_run is deliberately omitted: the API schema marks it non-nullable, but
  // legacy registrations saved before it existed have null there, so requesting it errors
  // out the entire list ("Cannot return null for non-nullable field KnifeRegistration.is_founders_run").
  // Re-add it once the backend backfills those rows or relaxes the field to nullable.
  const KNIFE_REGISTRATION_NODE_FIELDS = 'id first_name last_name email phone address knife_model serial_number handle_material blade_steel purchase_date order_number purchase_location purchase_location_other founders_run_number certificate_number box_number customer_notes admin_notes knife_photo_url certificate_photo_url registration_status duplicate_serial_number created_at';
  const KNIFE_REGISTRATION_CONNECTION_FIELDS = `edges { cursor node { ${KNIFE_REGISTRATION_NODE_FIELDS} } } pageInfo { hasNextPage endCursor }`;
  const DEFAULT_STORIES_QUERY = `query AdminStories($filter: StoriesFilterInput) { adminStories(filter: $filter) { ${STORY_CONNECTION_FIELDS} } }`;
  const DEFAULT_CATEGORIES_QUERY = 'query { categories { id slug label sort_order } }';
  const DEFAULT_DROPS_QUERY = `query AdminDrops($filter: AdminDropsFilterInput) { adminDrops(filter: $filter) { ${DROP_CONNECTION_FIELDS} } }`;
  const DEFAULT_DROP_COLLECTIONS_QUERY = `query AdminDropCollections($search: String!) { adminDropCollections(search: $search) { ${DROP_COLLECTION_FIELDS} } }`;
  const DEFAULT_KNIFE_REGISTRATIONS_QUERY = `query AdminKnifeRegistrations($filter: KnifeRegistrationsFilterInput) { adminKnifeRegistrations(filter: $filter) { ${KNIFE_REGISTRATION_CONNECTION_FIELDS} } }`;
  // Confirmed against the real API. Returns the CSV content directly as a string.
  const DEFAULT_EXPORT_KNIFE_REGISTRATIONS_CSV_QUERY = 'query ExportAdminCsv($filter: KnifeRegistrationsFilterInput) { exportKnifeRegistrationsCsv(filter: $filter) }';
  // Confirmed against the real API (mutation name/shape/multipart upload format all match).
  // updateKnifeRegistration is shared by both the Change Status action and the full Edit form,
  // same as updateStory serves both story review and story editing.
  const DEFAULT_UPDATE_KNIFE_REGISTRATION_MUTATION = `mutation UpdateKnifeRegistration($id: ID!, $input: UpdateKnifeRegistrationInput!) { updateKnifeRegistration(id: $id, input: $input) { ${KNIFE_REGISTRATION_NODE_FIELDS} } }`;
  // Confirmed against the real API (field name/scalar-return shape match; parameterized here
  // with a variable instead of the curl's inlined literal id, which is equivalent and safer).
  const DEFAULT_DELETE_KNIFE_REGISTRATION_MUTATION = 'mutation DeleteKnifeRegistration($id: ID!) { deleteKnifeRegistration(id: $id) }';
  const DEFAULT_CREATE_STORY_MUTATION = `mutation CreateStory($input: CreateStoryInput!) { createStory(input: $input) { ${STORY_NODE_FIELDS} } }`;
  const DEFAULT_UPDATE_STORY_MUTATION = `mutation UpdateStory($id: ID!, $input: UpdateStoryInput!) { updateStory(id: $id, input: $input) { ${STORY_NODE_FIELDS} } }`;
  const DEFAULT_DELETE_STORY_MUTATION = 'mutation DeleteStory($id: ID!) { deleteStory(id: $id) }';
  const DEFAULT_CREATE_DROP_MUTATION = `mutation CreateDrop($input: CreateDropInput!) { createDrop(input: $input) { ${DROP_FIELDS} } }`;
  const DEFAULT_UPDATE_DROP_MUTATION = `mutation UpdateDrop($id: ID!, $input: UpdateDropInput!) { updateDrop(id: $id, input: $input) { ${DROP_FIELDS} } }`;
  const DEFAULT_CANCEL_DROP_MUTATION = `mutation CancelDrop($id: ID!) { cancelDrop(id: $id) { ${DROP_FIELDS} } }`;
  const DEFAULT_SIGNOUT_MUTATION = 'mutation Signout($input: SignoutInput!) { signout(input: $input) }';

  function cleanFilter(filter) {
    return Object.keys(filter || {}).reduce((result, key) => {
      if (filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
        result[key] = filter[key];
      }

      return result;
    }, {});
  }

  function extractGraphqlErrorMessage(error) {
    if (!error) return '';

    const extensions = error.extensions || {};
    const nested = extensions.originalError || extensions.exception || extensions.response || {};
    const detail = nested.message || extensions.message;

    if (Array.isArray(detail)) {
      return detail.filter(Boolean).join(' ');
    }

    if (detail) {
      return String(detail);
    }

    return error.message || '';
  }

  function isThrottleMessage(message) {
    return /ThrottlerException|Too Many Requests|rate limit|rate-limit|429/i.test(String(message || ''));
  }

  function extractToken(value) {
    if (!value) return '';

    const rawValue = String(value).trim();
    if (!rawValue) return '';

    try {
      const parsed = JSON.parse(rawValue);
      if (parsed && typeof parsed === 'object') {
        return extractToken(parsed.accessToken || parsed.token || parsed.authToken || parsed.jwt);
      }
    } catch (error) {
      // Stored token is already a plain string.
    }

    return rawValue.replace(/^Bearer\s+/i, '').trim();
  }

  function getStoryConnection(data) {
    if (!data || typeof data !== 'object') return null;

    return data.adminStories ||
      data.stories ||
      data.allStories ||
      data.myStories ||
      Object.keys(data)
        .map((key) => data[key])
        .find((value) => value && typeof value === 'object' && (Array.isArray(value.edges) || Array.isArray(value.nodes))) ||
      null;
  }

  function getStoryResult(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.createStory) return data.createStory;
    if (data.updateStory) return data.updateStory;

    return Object.keys(data)
      .map((key) => data[key])
      .find((value) => value && typeof value === 'object' && value.id) ||
      null;
  }

  function getDropNodes(data) {
    if (!data || typeof data !== 'object') return [];

    const source = data.adminDrops ||
      data.drops ||
      data.allDrops ||
      Object.keys(data)
        .map((key) => data[key])
        .find((value) => Array.isArray(value) || (value && typeof value === 'object' && (Array.isArray(value.edges) || Array.isArray(value.nodes))));

    if (Array.isArray(source)) return source;
    if (source && Array.isArray(source.nodes)) return source.nodes;
    if (source && Array.isArray(source.edges)) {
      return source.edges.map((edge) => edge && edge.node ? edge.node : edge).filter(Boolean);
    }

    return [];
  }

  function getKnifeRegistrationConnection(data) {
    if (!data || typeof data !== 'object') return null;

    return data.adminKnifeRegistrations ||
      Object.keys(data)
        .map((key) => data[key])
        .find((value) => value && typeof value === 'object' && Array.isArray(value.edges)) ||
      null;
  }

  function getKnifeRegistrationResult(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.updateKnifeRegistration) return data.updateKnifeRegistration;

    return Object.keys(data)
      .map((key) => data[key])
      .find((value) => value && typeof value === 'object' && value.id) ||
      null;
  }

  function getDropResult(data) {
    if (!data || typeof data !== 'object') return null;
    if (data.createDrop) return data.createDrop;
    if (data.updateDrop) return data.updateDrop;
    if (data.cancelDrop) return data.cancelDrop;

    return Object.keys(data)
      .map((key) => data[key])
      .find((value) => value && typeof value === 'object' && value.id) ||
      null;
  }

  function getDropCollectionNodes(data) {
    if (!data || typeof data !== 'object') return [];

    const source = data.adminDropCollections ||
      data.dropCollections ||
      data.collections ||
      Object.keys(data)
        .map((key) => data[key])
        .find((value) => Array.isArray(value) || (value && typeof value === 'object' && (Array.isArray(value.edges) || Array.isArray(value.nodes))));

    if (Array.isArray(source)) return source;
    if (source && Array.isArray(source.nodes)) return source.nodes;
    if (source && Array.isArray(source.edges)) {
      return source.edges.map((edge) => edge && edge.node ? edge.node : edge).filter(Boolean);
    }

    return [];
  }

  class AdminPortalApi {
    constructor(options) {
      const config = window.TKC && window.TKC.authConfig ? window.TKC.authConfig : {};
      const opts = options || {};

      this.endpoint = opts.endpoint || config.endpoint;
      this.tokenKey = opts.tokenKey || 'tkcAdminAccessToken';
      this.tokenFallbackKeys = [
        this.tokenKey,
        ...(Array.isArray(opts.tokenFallbackKeys) ? opts.tokenFallbackKeys : []),
        'tkcAdminAccessToken',
        'adminAccessToken',
        'tkcAdminToken'
      ].filter((key, index, keys) => key && keys.indexOf(key) === index);
      this.storiesQuery = opts.storiesQuery || DEFAULT_STORIES_QUERY;
      this.categoriesQuery = opts.categoriesQuery || DEFAULT_CATEGORIES_QUERY;
      this.dropsQuery = opts.dropsQuery || DEFAULT_DROPS_QUERY;
      this.dropCollectionsQuery = opts.dropCollectionsQuery || DEFAULT_DROP_COLLECTIONS_QUERY;
      this.knifeRegistrationsQuery = opts.knifeRegistrationsQuery || DEFAULT_KNIFE_REGISTRATIONS_QUERY;
      this.exportKnifeRegistrationsCsvQuery = opts.exportKnifeRegistrationsCsvQuery || DEFAULT_EXPORT_KNIFE_REGISTRATIONS_CSV_QUERY;
      this.updateKnifeRegistrationMutation = opts.updateKnifeRegistrationMutation || DEFAULT_UPDATE_KNIFE_REGISTRATION_MUTATION;
      this.deleteKnifeRegistrationMutation = opts.deleteKnifeRegistrationMutation || DEFAULT_DELETE_KNIFE_REGISTRATION_MUTATION;
      this.createStoryMutation = opts.createStoryMutation || DEFAULT_CREATE_STORY_MUTATION;
      this.updateStoryMutation = opts.updateStoryMutation || DEFAULT_UPDATE_STORY_MUTATION;
      this.deleteStoryMutation = opts.deleteStoryMutation || DEFAULT_DELETE_STORY_MUTATION;
      this.createDropMutation = opts.createDropMutation || DEFAULT_CREATE_DROP_MUTATION;
      this.updateDropMutation = opts.updateDropMutation || DEFAULT_UPDATE_DROP_MUTATION;
      this.cancelDropMutation = opts.cancelDropMutation || DEFAULT_CANCEL_DROP_MUTATION;
      this.signoutMutation = opts.signoutMutation || DEFAULT_SIGNOUT_MUTATION;
    }

    getToken() {
      for (let index = 0; index < this.tokenFallbackKeys.length; index += 1) {
        const token = extractToken(window.localStorage.getItem(this.tokenFallbackKeys[index]));
        if (token) return token;
      }

      return '';
    }

    requireToken() {
      const token = this.getToken();
      if (!token) {
        throw new Error('Please log in to view the admin portal.');
      }
      return token;
    }

    getAuthHeaders(extraHeaders, options) {
      const headers = Object.assign({
        Accept: 'application/json',
        'apollo-require-preflight': 'true',
        'ngrok-skip-browser-warning': 'true'
      }, extraHeaders || {});

      if (!options || options.auth !== false) {
        headers.Authorization = `Bearer ${this.requireToken()}`;
      }

      return headers;
    }

    async request(query, variables, options) {
      if (!this.endpoint) {
        throw new Error('Admin portal API endpoint is missing.');
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }, options),
        body: JSON.stringify({ query, variables: variables || {} })
      });

      return this.parseResponse(response);
    }

    async parseResponse(response) {
      const payload = await response.json().catch(() => null);

      if (payload && payload.errors && payload.errors.length) {
        const messages = payload.errors.map(extractGraphqlErrorMessage).filter(Boolean);
        const message = messages.join(' ') || 'Request failed. Please try again.';
        const error = new Error(isThrottleMessage(message) ? 'API rate limit reached. Please wait about a minute and try again.' : message);
        error.isThrottled = isThrottleMessage(message);
        throw error;
      }

      if (!response.ok) {
        const message = (payload && payload.message) || 'Request failed. Please try again.';
        const error = new Error(response.status === 429 || isThrottleMessage(message) ? 'API rate limit reached. Please wait about a minute and try again.' : message);
        error.isThrottled = response.status === 429 || isThrottleMessage(message);
        throw error;
      }

      return payload ? payload.data : null;
    }

    async stories(filter) {
      const variables = { filter: cleanFilter(filter) };
      const data = await this.request(this.storiesQuery, variables);
      return getStoryConnection(data) || { edges: [], pageInfo: {} };
    }

    async categories() {
      const data = await this.request(this.categoriesQuery);
      return data && Array.isArray(data.categories) ? data.categories : [];
    }

    async drops() {
      const data = await this.request(this.dropsQuery);
      return getDropNodes(data);
    }

    async dropCollections(search) {
      const data = await this.request(this.dropCollectionsQuery, { search: search || '' });
      return getDropCollectionNodes(data);
    }

    async knifeRegistrations(filter) {
      const variables = { filter: cleanFilter(filter) };
      const data = await this.request(this.knifeRegistrationsQuery, variables);
      return getKnifeRegistrationConnection(data) || { edges: [], pageInfo: {} };
    }

    async exportKnifeRegistrationsCsv(filter) {
      const variables = { filter: cleanFilter(filter) };
      const data = await this.request(this.exportKnifeRegistrationsCsvQuery, variables);
      return data ? data.exportKnifeRegistrationsCsv : null;
    }

    // photoChanges is an optional { knife_photo, certificate_photo } map where each value is:
    //   - a File/Blob  -> uploaded via multipart, sent as `<key>: null` in the input
    //   - 'remove'     -> sends `remove_<key>: true` instead of `<key>: null`
    //   - omitted      -> field left untouched
    async updateKnifeRegistration(id, input, photoChanges) {
      const cleanedInput = Object.assign({}, input);
      const uploads = [];

      ['knife_photo', 'certificate_photo'].forEach((key) => {
        const change = photoChanges && photoChanges[key];
        if (change instanceof Blob) {
          uploads.push({ key, file: change });
          cleanedInput[key] = null;
        } else if (change === 'remove') {
          cleanedInput[`remove_${key}`] = true;
        }
      });

      const variables = { id, input: cleanedInput };

      if (!uploads.length) {
        const data = await this.request(this.updateKnifeRegistrationMutation, variables);
        return getKnifeRegistrationResult(data);
      }

      const map = {};
      uploads.forEach((upload, index) => {
        map[index] = [`variables.input.${upload.key}`];
      });

      const formData = new FormData();
      formData.append('operations', JSON.stringify({ query: this.updateKnifeRegistrationMutation, variables }));
      formData.append('map', JSON.stringify(map));
      uploads.forEach((upload, index) => {
        formData.append(String(index), upload.file);
      });

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders({ 'apollo-require-preflight': 'true' }),
        body: formData
      });

      const data = await this.parseResponse(response);
      return getKnifeRegistrationResult(data);
    }

    async deleteKnifeRegistration(id) {
      const data = await this.request(this.deleteKnifeRegistrationMutation, { id });
      return data && Object.prototype.hasOwnProperty.call(data, 'deleteKnifeRegistration') ? data.deleteKnifeRegistration : data;
    }

    buildStoryMedia(mediaItems) {
      const entries = (mediaItems || []).filter(Boolean);
      const uploads = [];

      const media = entries.map((entry, index) => {
        const file = entry instanceof Blob ? entry : entry.file;
        const sortOrder = typeof entry.sort_order === 'number' ? entry.sort_order : index;

        if (file) {
          uploads.push({ file, mediaIndex: index });
          return {
            file: null,
            alt: entry.alt || file.name || `Media ${index + 1}`,
            media_type: file.type && file.type.indexOf('video/') === 0 ? 'VIDEO' : 'IMAGE',
            sort_order: sortOrder
          };
        }

        if (entry.id) {
          const item = { id: entry.id, sort_order: sortOrder };
          if (entry.alt) item.alt = entry.alt;
          return item;
        }

        const item = { url: entry.url, sort_order: sortOrder };
        if (entry.alt) item.alt = entry.alt;
        if (entry.media_type) item.media_type = entry.media_type;
        return item;
      });

      return { media, uploads };
    }

    async sendStoryMultipart(query, variables, uploads, rootField) {
      if (!this.endpoint) {
        throw new Error('Admin portal API endpoint is missing.');
      }

      const map = {};
      uploads.forEach((upload, uploadIndex) => {
        map[uploadIndex] = [`variables.input.media.${upload.mediaIndex}.file`];
      });

      const formData = new FormData();
      formData.append('operations', JSON.stringify({ query, variables }));
      formData.append('map', JSON.stringify(map));
      uploads.forEach((upload, uploadIndex) => {
        formData.append(String(uploadIndex), upload.file);
      });

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.getAuthHeaders({ 'apollo-require-preflight': 'true' }),
        body: formData
      });

      const data = await this.parseResponse(response);
      return getStoryResult({ [rootField]: data && data[rootField] ? data[rootField] : null });
    }

    async createStory(input, mediaItems) {
      const { media, uploads } = this.buildStoryMedia(mediaItems);
      const variables = { input: Object.assign({}, input, { media }) };
      return this.sendStoryMultipart(this.createStoryMutation, variables, uploads, 'createStory');
    }

    async updateStory(id, input, mediaItems) {
      if (Array.isArray(mediaItems)) {
        const { media, uploads } = this.buildStoryMedia(mediaItems);
        const variables = { id, input: Object.assign({}, input, { media }) };
        return this.sendStoryMultipart(this.updateStoryMutation, variables, uploads, 'updateStory');
      }

      const data = await this.request(this.updateStoryMutation, { id, input });
      return getStoryResult(data);
    }

    async deleteStory(id) {
      const data = await this.request(this.deleteStoryMutation, { id });
      return data && Object.prototype.hasOwnProperty.call(data, 'deleteStory') ? data.deleteStory : data;
    }

    async createDrop(input) {
      const data = await this.request(this.createDropMutation, { input });
      return getDropResult(data);
    }

    async updateDrop(id, input) {
      const data = await this.request(this.updateDropMutation, { id, input });
      return getDropResult(data);
    }

    async cancelDrop(id) {
      const data = await this.request(this.cancelDropMutation, { id });
      return getDropResult(data);
    }

    async signout(deviceId) {
      return this.request(this.signoutMutation, { input: { device_id: deviceId } });
    }
  }

  window.TKC = window.TKC || {};
  window.TKC.AdminPortalApi = AdminPortalApi;
})();
